import Papa from 'papaparse';

export const ExcelErrors = {
  DUPLICATE_EMAIL_IN_FILE: 'errors.notifications.DUPLICATE_EMAIL_IN_FILE',
  DUPLICATE_PHONE_IN_FILE: 'errors.notifications.DUPLICATE_PHONE_IN_FILE',
  INCORRECT_EMAIL: 'telemed.invalidEmail',
  MAX_CELL_SIZE_EXCEED: 'errors.notifications.MAX_CELL_SIZE_EXCEED',
  NOT_SUPPORTED_DATE_FORMAT: 'errors.notifications.NOT_SUPPORTED_DATE_FORMAT',
  NOT_SUPPORTED_VALUE: 'errors.notifications.NOT_SUPPORTED_VALUE',
  IS_REQUIRED: 'errors.notifications.IS_REQUIRED',
  UNDEFINED: 'errors.notifications.UNDEFINED',
  INCORRECT_PHONE: 'errors.notifications.INCORRECT_PHONE',
  IS_NOT_UNIQUE: 'errors.notifications.IS_NOT_UNIQUE',
  INVALID_UUID: 'errors.notifications.INVALID_UUID',
  LINK_PROFILE_NOT_FOUND: 'errors.notifications.LINK_PROFILE_NOT_FOUND',
  POLICY_BELONGS_TO_ANOTHER_PROGRAM: 'errors.notifications.POLICY_BELONGS_TO_ANOTHER_PROGRAM',
  POLICY_BELONGS_TO_ANOTHER_CLIENT: 'errors.notifications.POLICY_BELONGS_TO_ANOTHER_CLIENT',
  VALUE_NOT_MATCH_EXISTING_VALUE: 'errors.notifications.VALUE_NOT_MATCH_EXISTING_VALUE',
  IDENTIFICATION_NUMBER_IS_RESERVED: 'errors.notifications.IDENTIFICATION_NUMBER_IS_RESERVED',
  INCORRECT_NUMBER: 'errors.notifications.INCORRECT_NUMBER',
  INCORRECT_AMOUNT: 'errors.notifications.INCORRECT_AMOUNT',
  AGENT_EMAIL_NOT_FOUND: 'errors.notifications.AGENT_EMAIL_NOT_FOUND',
  INVALID_CURRENCY_CODE: 'errors.notifications.INVALID_CURRENCY_CODE',
  NEGATIVE_NUMBER_NOT_ALLOWED: 'errors.notifications.NEGATIVE_NUMBER_NOT_ALLOWED',
  POLICY_INSURANCE_TYPE_MISMATCH: 'errors.notifications.POLICY_INSURANCE_TYPE_MISMATCH',
  PHONE_ALREADY_IN_USE: 'errors.notifications.PHONE_ALREADY_IN_USE',
  EMAIL_ALREADY_IN_USE: 'errors.notifications.EMAIL_ALREADY_IN_USE',
  // Added for Payments Review and Excel imports
  NAME_DOES_NOT_MATCH: 'errors.notifications.NAME_DOES_NOT_MATCH',
  SURNAME_DOES_NOT_MATCH: 'errors.notifications.SURNAME_DOES_NOT_MATCH',
  PAYMENT_DATE_DOES_NOT_MATCH: 'errors.notifications.PAYMENT_DATE_DOES_NOT_MATCH',
  POLICY_NUMBER_MISSING: 'errors.notifications.POLICY_NUMBER_MISSING',
  FULL_NAME_MISSING: 'errors.notifications.FULL_NAME_MISSING',
  PAYMENT_AMOUNT_DOES_NOT_MATCH: 'errors.notifications.PAYMENT_AMOUNT_DOES_NOT_MATCH',
  MULTIPLE_POLICIES_FOUND: 'errors.notifications.MULTIPLE_POLICIES_FOUND',
  POLICY_NOT_FOUND: 'errors.notifications.POLICY_NOT_FOUND',
  PROFILE_NOT_FOUND: 'errors.notifications.PROFILE_NOT_FOUND',
  // REN-1288: phone column validated as text
  PHONE_INVALID_LENGTH: 'errors.notifications.PHONE_INVALID_LENGTH',
  PHONE_SCIENTIFIC_NOTATION: 'errors.notifications.PHONE_SCIENTIFIC_NOTATION',
  // Phone is not a valid number for the country its digits imply (usually a
  // missing country code). Blocking, like the other phone errors.
  PHONE_INVALID_NUMBER: 'errors.notifications.PHONE_INVALID_NUMBER',
};
// IS_NOT_UNIQUE is generic, but when the backend reports a duplicate against an
// existing profile it includes that person's name and id in `details`. In that case
// show a richer message linking to the original profile that already holds the value.
export function getExcelErrorKey(error) {
  const details = error?.details || {};
  if (error?.code === 'IS_NOT_UNIQUE' && details.existingPersonProfileId) {
    return 'errors.notifications.IS_NOT_UNIQUE_PROFILE_CONFLICT';
  }
  // PHONE_INVALID_NUMBER has a dial code when the digits imply a country, and none
  // when there is no valid country code at all — different copy for each case.
  if (error?.code === 'PHONE_INVALID_NUMBER' && !details.dialCode) {
    return 'errors.notifications.PHONE_INVALID_NUMBER_NO_CODE';
  }
  return ExcelErrors[error?.code];
}
// Non-blocking import warnings. Same {row, col, code, details} shape as errors, but
// they never block a commit. Unknown codes fall back to details.message.
export const ExcelWarnings = {
  PHONE_FOREIGN_COUNTRY: 'errors.notifications.PHONE_FOREIGN_COUNTRY',
};
export function getExcelWarningKey(warning) {
  return ExcelWarnings[warning?.code];
}
// Resolve a warning to display text: mapped i18n key if known, otherwise the
// backend-provided details.message (always populated).
export function getExcelWarningText(warning, translate) {
  const key = getExcelWarningKey(warning);
  if (key) {
    return translate(key, { ...(warning?.details || {}) });
  }
  return warning?.details?.message || '';
}
export const ExcelErrorsWithoutLink = {
  EMAIL_ALREADY_IN_USE: 'errors.notifications.without_links.EMAIL_ALREADY_IN_USE',
  PHONE_ALREADY_IN_USE: 'errors.notifications.without_links.PHONE_ALREADY_IN_USE',
  POLICY_BELONGS_TO_ANOTHER_CLIENT: 'errors.notifications.without_links.POLICY_BELONGS_TO_ANOTHER_CLIENT',
  POLICY_BELONGS_TO_ANOTHER_PROGRAM: 'errors.notifications.without_links.POLICY_BELONGS_TO_ANOTHER_PROGRAM',
  VALUE_NOT_MATCH_EXISTING_VALUE: 'errors.notifications.without_links.VALUE_NOT_MATCH_EXISTING_VALUE'
};
export const ErrorsWithProfileLink = [
  'EMAIL_ALREADY_IN_USE',
  'PHONE_ALREADY_IN_USE',
  'POLICY_BELONGS_TO_ANOTHER_CLIENT',
  'POLICY_BELONGS_TO_ANOTHER_PROGRAM',
  'VALUE_NOT_MATCH_EXISTING_VALUE'
];
// Conflicts that can be resolved by merging the two profiles right from the
// error modal (requires details.currentProfileId + details.conflictingProfileId)
export const MergeableErrorCodes = [
  'EMAIL_ALREADY_IN_USE',
  'PHONE_ALREADY_IN_USE'
];
// Same value appears on several file rows — rendered as grouped rows with raw
// file context instead of the flat Row/Value list
export const DuplicateInFileErrorCodes = [
  'DUPLICATE_EMAIL_IN_FILE',
  'DUPLICATE_PHONE_IN_FILE'
];
export const ExcelErrorsTitle = {
  DUPLICATE_EMAIL_IN_FILE: 'errors.notifications.DUPLICATE_EMAIL_IN_FILE',
  DUPLICATE_PHONE_IN_FILE: 'errors.notifications.DUPLICATE_PHONE_IN_FILE',
  INCORRECT_EMAIL: 'telemed.invalidEmail',
  MAX_CELL_SIZE_EXCEED: 'errors.notifications.MAX_CELL_SIZE_EXCEED',
  NOT_SUPPORTED_DATE_FORMAT: 'errors.notifications.NOT_SUPPORTED_DATE_FORMAT',
  NOT_SUPPORTED_VALUE: 'errors.notifications.NOT_SUPPORTED_VALUE',
  IS_REQUIRED: 'errors.notifications.IS_REQUIRED',
  UNDEFINED: 'errors.notifications.UNDEFINED',
  INCORRECT_PHONE: 'errors.notifications.INCORRECT_PHONE',
  IS_NOT_UNIQUE: 'errors.notifications.IS_NOT_UNIQUE',
  INVALID_UUID: 'errors.notifications.INVALID_UUID',
  LINK_PROFILE_NOT_FOUND: 'errors.notifications.LINK_PROFILE_NOT_FOUND',
  POLICY_BELONGS_TO_ANOTHER_PROGRAM: 'errors.notifications.POLICY_BELONGS_TO_ANOTHER_PROGRAM',
  POLICY_BELONGS_TO_ANOTHER_CLIENT: 'errors.notifications.POLICY_BELONGS_TO_ANOTHER_CLIENT',
  VALUE_NOT_MATCH_EXISTING_VALUE: 'errors.notifications.VALUE_NOT_MATCH_EXISTING_VALUE',
  IDENTIFICATION_NUMBER_IS_RESERVED: 'errors.notifications.IDENTIFICATION_NUMBER_IS_RESERVED',
  INCORRECT_NUMBER: 'errors.notifications.INCORRECT_NUMBER',
  INCORRECT_AMOUNT: 'errors.notifications.INCORRECT_AMOUNT',
  // Added titles for new errors
  NAME_DOES_NOT_MATCH: 'errors.notifications.NAME_DOES_NOT_MATCH',
  SURNAME_DOES_NOT_MATCH: 'errors.notifications.SURNAME_DOES_NOT_MATCH',
  PAYMENT_DATE_DOES_NOT_MATCH: 'errors.notifications.PAYMENT_DATE_DOES_NOT_MATCH',
  POLICY_NUMBER_MISSING: 'errors.notifications.POLICY_NUMBER_MISSING',
  FULL_NAME_MISSING: 'errors.notifications.FULL_NAME_MISSING',
  PAYMENT_AMOUNT_DOES_NOT_MATCH: 'errors.notifications.PAYMENT_AMOUNT_DOES_NOT_MATCH',
  MULTIPLE_POLICIES_FOUND: 'errors.notifications.MULTIPLE_POLICIES_FOUND',
  POLICY_NOT_FOUND: 'errors.notifications.POLICY_NOT_FOUND',
  PROFILE_NOT_FOUND: 'errors.notifications.PROFILE_NOT_FOUND',
  // REN-1288: phone column validated as text
  PHONE_INVALID_LENGTH: 'errors.notifications.PHONE_INVALID_LENGTH',
  PHONE_SCIENTIFIC_NOTATION: 'errors.notifications.PHONE_SCIENTIFIC_NOTATION',
  PHONE_INVALID_NUMBER: 'errors.notifications.PHONE_INVALID_NUMBER'
};
export const ExcelTableTypes = {
  INSURED_USERS: 'INSURED_USERS',
  INVESTING_INSURED_USERS: 'INVESTING_INSURED_USERS',
  INSURED_USERS_WITHOUT_POLICY: 'INSURED_USERS_WITHOUT_POLICY'
};
export const FileFields = ['policy_file', 'certificate_file'];
export const ExcelDefaultColumnsData = {
  [ExcelTableTypes.INSURED_USERS]: {
    fields: [
      'coverage',
      'id_number',
      'first_name',
      'last_name',
      'patronymic_name',
      'birthdate',
      'phone',
      'email',
      'carrier',
      'policy_number',
      'certificate_number',
      'start_date',
      'end_date',
      'insured_role',
      'link_id_number',
      'agent_email',
      'agent_commission',
      'agent_commission_payment_format',
      'deal_premium',
      'currency_code',
      'payment_format',
      'policy_file',
      'certificate_file',
    ],
    requiredFields: ['last_name', 'carrier', 'start_date', 'end_date', 'id_number']
  },
  [ExcelTableTypes.INVESTING_INSURED_USERS]: {
    fields: [
      'coverage',
      'id_number',
      'first_name',
      'last_name',
      'patronymic_name',
      'birthdate',
      'phone',
      'email',
      'carrier',
      'final_balance',
      'initial_balance',
      'invest_start_date',
      'monthly_deposit',
      'current_monthly_deposit',
      'policy_number',
      'certificate_number',
      'agent_email',
      'agent_commission',
      'agent_commission_payment_format',
      'deal_premium',
      'currency_code',
      'payment_format',
      'start_date',
      'end_date',
      'insured_role',
      'link_id_number',
      'policy_file',
      'certificate_file',
    ],
    requiredFields: [
      'id_number',
      'last_name',
      'carrier',
      'final_balance',
      'initial_balance',
      'invest_start_date',
      'monthly_deposit',
      'current_monthly_deposit',
    ]
  },
  [ExcelTableTypes.INSURED_USERS_WITHOUT_POLICY]: {
    fields: [
      'first_name',
      'last_name',
      'patronymic_name',
      'birthdate',
      'phone',
      'email',
      'id_number',
      'insured_role',
      'link_id_number',
    ],
    requiredFields: ['last_name']
  }
};

// AHORRO / INVERSION name the same premium amount differently (matching the
// web form labels); the backend accepts these as aliases of deal_premium.
const DEAL_PREMIUM_COLUMN_BY_TYPE = {
  AHORRO: 'plan_value',
  INVERSION: 'money_deposited',
};

export function getExcelDefaultColumns(tableType, programId = null, insuranceCompanyId = null, isGroupPolicy = false, insuranceType = null) {
  const data = ExcelDefaultColumnsData[tableType];
  if (!data) return { fields: [], requiredFields: [] };

  const filterFn = (val) => {
    if (programId && (val.includes('coverage') || val.includes('plan'))) {
      return false;
    }
    if (insuranceCompanyId && val.includes('carrier')) {
      return false;
    }
    return true;
  };

  let requiredFields = data.requiredFields.filter(filterFn);

  if (isGroupPolicy) {
    requiredFields = requiredFields.filter((field) => !['start_date', 'end_date'].includes(field));
  }

  const dealPremiumColumn = DEAL_PREMIUM_COLUMN_BY_TYPE[insuranceType];
  const renameFn = (val) => (dealPremiumColumn && val === 'deal_premium' ? dealPremiumColumn : val);

  return {
    fields: data.fields.filter(filterFn).map(renameFn),
    requiredFields: requiredFields.map(renameFn),
  };
}

export function getExcelDataWithRequiredMark(data, excelTableTypes, fileNames = {}, programId = null, insuranceCompanyId = null, isGroupPolicy = false, insuranceType = null) {
  const { fields, requiredFields } = getExcelDefaultColumns(excelTableTypes, programId, insuranceCompanyId, isGroupPolicy, insuranceType);
  return data.map((list, rowIndex) => {
    if (rowIndex === 0) {
      return fields.map((item) => {
        if (requiredFields.includes(item)) {
          return `${item}*`;
        }
        return item;
      });
    }
    return list.map((val, columnIndex) => {
      const fileName = fileNames[`${columnIndex}-${rowIndex}`];
      if (fileName) {
        return fileName;
      }
      return val;
    });
  });
}

export function getExcelColumnName(index) {
  let columnName = '';
  let columnIndex = index;
  while (columnIndex >= 0) {
    const remainder = columnIndex % 26;
    columnName = String.fromCharCode(65 + remainder) + columnName;
    columnIndex = Math.floor(columnIndex / 26) - 1;
  }
  return columnName;
}

export function parseFileCsvData(event) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const csvJsonString = event.target.result;
      const csvJson = Papa.parse(csvJsonString.toString(), {
        header: true,
        skipEmptyLines: true,
      });
      const columns = csvJson.meta.fields;
      const values = csvJson.data.map((row) => columns.map((column) => row[column]));
      const tableData = [columns, ...values];
      resolve(tableData);
    });
    reader.addEventListener('error', (error) => reject(error));
    reader.readAsText(event.target.files[0]);
  });
}

// The backend reports error.row as a 1-based data-row index (header excluded)
// and skips empty lines while parsing, so a Papa parse with header:false and
// skipEmptyLines:true yields rawRows where rawRows[0] is the header row and
// rawRows[error.row] is exactly the failing line.
export function parseRawFileRows(file) {
  if (!file || !file.name || !file.name.toLowerCase().endsWith('.csv')) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = (result?.data || []).filter((row) => Array.isArray(row));
        resolve(rows.length > 1 ? rows : null);
      },
      error: () => resolve(null),
    });
  });
}

// Builds a compact excerpt of the file around the error rows: each error row
// with ±span neighbours, overlapping windows merged, far-apart windows
// separated by an {isGap: true} marker row.
export function getRawRowContext(rawRows, errorRows, span = 2) {
  if (!Array.isArray(rawRows) || rawRows.length < 2) return null;
  const targets = Array.from(new Set((Array.isArray(errorRows) ? errorRows : [errorRows])
    .filter((row) => Number.isInteger(row) && row >= 1 && row < rawRows.length)))
    .sort((a, b) => a - b);
  if (!targets.length) return null;

  const ranges = [];
  targets.forEach((target) => {
    const from = Math.max(1, target - span);
    const to = Math.min(rawRows.length - 1, target + span);
    const last = ranges[ranges.length - 1];
    if (last && from <= last.to + 1) {
      last.to = Math.max(last.to, to);
    } else {
      ranges.push({ from, to });
    }
  });

  const rows = [];
  ranges.forEach((range, rangeIndex) => {
    if (rangeIndex > 0) {
      rows.push({
        isGap: true, rowNumber: null, cells: [], isErrorRow: false,
      });
    }
    for (let i = range.from; i <= range.to; i += 1) {
      rows.push({
        isGap: false,
        rowNumber: i,
        cells: rawRows[i],
        isErrorRow: targets.includes(i),
      });
    }
  });
  return { headerRow: rawRows[0], rows };
}

// Backend details.fieldName values that differ from the mapping keys used by
// the upload wizard (mapping = { mappingKey: fileColumnHeader })
const FieldNameMappingAliases = {
  phone: ['phone', 'phone_number'],
  identification_number: ['identification_number', 'id_number'],
  id_number: ['id_number', 'identification_number'],
};

// Resolves which raw-file column an error refers to. error.col can't be used:
// it indexes the backend's internal header set, not the uploaded file.
// Returns -1 when the column can't be determined (highlight the whole row).
export function resolveErrorColumnIndex(headerRow, details, mappedResult, errorRowCells = null) {
  if (!Array.isArray(headerRow) || !details?.fieldName) return -1;
  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  const aliases = FieldNameMappingAliases[details.fieldName] || [details.fieldName];

  const byMapping = aliases
    .map((alias) => mappedResult?.[alias])
    .filter((fileField) => fileField && fileField !== 'skip')
    .map((fileField) => headerRow.findIndex((header) => normalize(header) === normalize(fileField)))
    .find((index) => index !== -1);
  if (Number.isInteger(byMapping)) return byMapping;

  const byHeaderName = aliases
    .map((alias) => headerRow.findIndex((header) => normalize(header) === normalize(alias)))
    .find((index) => index !== -1);
  if (Number.isInteger(byHeaderName)) return byHeaderName;

  if (Array.isArray(errorRowCells) && details.value) {
    const byValue = errorRowCells.findIndex((cell) => normalize(cell) === normalize(details.value));
    if (byValue !== -1) return byValue;
  }
  return -1;
}

export function getPreparedArrayData(arr, fileIds) {
  const preparedList = arr.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return row.map((item) => item.replace('*', ''));
    }
    return row.map((item, colIndex) => {
      const fileIdKey = `${colIndex}-${rowIndex}`;
      const fileId = fileIds[fileIdKey];
      if (fileId) {
        return fileId;
      }
      return item;
    });
  });
  return preparedList.filter((row) => row.some((val) => !!val));
}
